import Cookies, { CookieAttributes } from 'js-cookie'

import { endpoints } from './../../endpoints'
import { diversioApi, diversioApiV2 } from './../../axios/axiosInstance'
import {
    AuthResponse,
    AuthToken,
    EnableMfaMutationPayload,
    EnableMfaResponse,
    InitializeSsoPayload,
    InitializeSsoResponse,
    LoginPayload,
    LoginResponse,
    RequestPasswordResetPayload,
    RequestVerificationCodePayload,
    ResetPasswordPayload,
    SsoCallbackPayload,
    ValidateVerificationCodePayload,
} from './authActions.types'

export const getTokenCookieName = (type: AuthToken): string => {
    const environment = process.env.REACT_APP_ENVIRONMENT || 'development'
    const envPrefix = environment === 'production' ? '' : `${environment}_`

    switch (type) {
        case 'access':
            return `${envPrefix}auth_access_token`
        case 'refresh':
            return `${envPrefix}auth_refresh_token`
        default:
            throw new Error('Invalid token type')
    }
}

const getCookieOptions = (includeDefaults = true): CookieAttributes => {
    const cookieDomain = process.env.REACT_APP_COOKIE_DOMAIN
    const cookieOptions: CookieAttributes = {}

    // Add default options for setting cookies
    if (includeDefaults) {
        cookieOptions.secure = true
        cookieOptions.sameSite = 'Lax'
    }

    // Only set domain if explicitly configured to avoid cross-subdomain issues
    if (cookieDomain && cookieDomain !== 'auto') {
        cookieOptions.domain = cookieDomain
    }

    return cookieOptions
}

export const setAuthToken = (token: string, type: AuthToken) => {
    const cookieOptions = getCookieOptions(true)
    Cookies.set(getTokenCookieName(type), token, cookieOptions)
}

export const getAuthToken = (type: AuthToken) => {
    return Cookies.get(getTokenCookieName(type))
}

export const removeAuthToken = (type: AuthToken) => {
    const cookieOptions = getCookieOptions(false)
    Cookies.remove(getTokenCookieName(type), cookieOptions)
}

export const loginAction = async (values: LoginPayload): Promise<LoginResponse> => {
    const response = await diversioApi.post(endpoints.auth.login, values)

    return response.data
}

export const requestPasswordResetAction = async (values: RequestPasswordResetPayload): Promise<AuthResponse> => {
    const response = await diversioApi.post(endpoints.auth.requestPasswordReset, values)

    return response.data
}

export const resetPasswordAction = async (values: ResetPasswordPayload): Promise<AuthResponse> => {
    const response = await diversioApi.post(endpoints.auth.resetPassword, values)

    return response.data
}

export const initializeSsoAction = async (values: InitializeSsoPayload): Promise<InitializeSsoResponse> => {
    const response = await diversioApi.post(endpoints.auth.initializeSso, values)

    return response.data
}

export const ssoCallbackAction = async (values: SsoCallbackPayload): Promise<LoginResponse> => {
    const response = await diversioApi.post(endpoints.auth.ssoCallback, values)

    return response.data
}

export const requestVerificationCodeAction = async (values: RequestVerificationCodePayload): Promise<AuthResponse> => {
    const response = await diversioApi.post(endpoints.auth.requestVerificationCode, values)

    return response.data
}

export const validateVerificationCodeAction = async (
    values: ValidateVerificationCodePayload,
): Promise<LoginResponse> => {
    const response = await diversioApi.post(endpoints.auth.validateVerificationCode, values)

    return response.data
}

export const requestEnableMfaAction = async ({
    userUUID,
    payload,
}: EnableMfaMutationPayload): Promise<EnableMfaResponse> => {
    const url = endpoints.auth.enableMfa(userUUID)
    const response = await diversioApiV2.post(url, payload)

    return response.data
}
